import { useState, useEffect, useCallback } from "react";
import { sendError } from "@/lib/client/sentry";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

/**
 * Hook for fetching currency exchange rates.
 *
 * Fetches exchange rates for converting between currencies.
 * Uses aggressive caching (24 hours) with 2-minute stale-while-revalidate.
 *
 * @returns Object containing:
 * - exchangeRates: Object containing exchange rate data
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function PriceDisplay({ amount, currency }) {
 *   const { exchangeRates, loading } = useGetExchangeRates();
 *
 *   if (loading) return <Spinner />;
 *
 *   const converted = convertCurrency(amount, currency, exchangeRates);
 *
 *   return <span>{converted}</span>;
 * }
 * ```
 */
const useGetExchangeRates = () => {
  const [loading, setLoading] = useState(false);
  const [exchangeRates, setExchangeRates] = useState();

  const fetchItem = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetch(`${API_URL}/api/v2/currencies`, {
        credentials: "include",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "public, max-age=86400, stale-while-revalidate=120",
        },
      }).then((response) => response.json());

      setExchangeRates(data.data);
    } catch (error) {
      console.warn("Error fetching exchange rates:", error);
      sendError(error as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  return {
    exchangeRates,
    loading,
  };
};

export default useGetExchangeRates;
